import { Link, useSearchParams } from "react-router-dom";

import { DataTable } from "../components/DataTable";
import { useFeedback } from "../components/Feedback";
import { IssueList } from "../components/IssueList";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { api, type Issue } from "../lib/api";
import { useApi } from "../lib/useApi";

interface Unmatched { id: number; source: string; code: string | null; text: string | null; occurrences: number; run_id: number }

export function DataQuality() {
  const open = useApi<Issue[]>("/issues?status=open");
  const resolved = useApi<Issue[]>("/issues?status=resolved");
  const unmatched = useApi<Unmatched[]>("/unmatched");
  const tabs = [
    { id: "open", label: "Open issues", count: open.data?.length },
    { id: "resolved", label: "Resolved", count: resolved.data?.length },
    { id: "unmatched", label: "Unmatched rows", count: unmatched.data?.length },
  ];
  const [tab, setTab] = useTab(tabs);
  const [params, setParams] = useSearchParams();
  const severity = params.get("severity") ?? "";
  const { toast } = useFeedback();

  const act = async (i: Issue, verb: "resolve" | "reopen") => {
    try {
      await api.post(`/issues/${i.id}/${verb}`);
      toast(verb === "resolve" ? "Marked resolved" : "Reopened");
      open.reload(); resolved.reload();
    } catch (e) { toast((e as Error).message, "error"); }
  };

  const list = (tab === "open" ? open.data : resolved.data)?.filter((i) => !severity || i.severity === severity);
  const err = open.error ?? resolved.error ?? unmatched.error;

  return (
    <>
      <PageHeader title="Data quality" sub="Every check the sources failed. Nothing is fixed silently — problems are recorded, shown here, and resolved by a person." />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {err && <ErrorBox error={err} />}
      {tab !== "unmatched" && (
        <Card actions={
          <select aria-label="Severity" value={severity} onChange={(e) => {
            const n = new URLSearchParams(params); if (e.target.value) n.set("severity", e.target.value); else n.delete("severity"); setParams(n, { replace: true });
          }}>
            <option value="">All severities</option><option value="error">Errors</option><option value="warning">Warnings</option><option value="info">Info</option>
          </select>}>
          {list ? <IssueList issues={list} onResolve={(i) => act(i, "resolve")} onReopen={(i) => act(i, "reopen")} /> : <Loading />}
        </Card>
      )}
      {tab === "unmatched" && (
        <Card>
          <p className="note muted">Rows no product could be matched to. They are kept, never dropped; each one is a gap in the crosswalk.</p>
          {unmatched.data ? (
            <DataTable rows={unmatched.data} rowKey={(u) => u.id} href={(u) => `/imports/${u.run_id}`} columns={[
              { key: "source", label: "Source" }, { key: "code", label: "Code", render: (u) => (u.code ? <code>{u.code}</code> : "—") },
              { key: "text", label: "Text", render: (u) => u.text ?? "—" }, { key: "occurrences", label: "Rows", numeric: true },
              { key: "run_id", label: "Import", render: (u) => <Link to={`/imports/${u.run_id}`}>#{u.run_id}</Link> },
            ]} />
          ) : <Loading />}
        </Card>
      )}
    </>
  );
}
